<script>
/**
 * CampanhaRelatorioModal — escolhe o que entra no relatório de respostas antes
 * de baixar.
 *
 * O arquivo sai do backend (`/campanha/relatorioRespostas/:id`), já montado com
 * as cores e o logotipo da marca do espaço. Aqui só se decide formato e seções:
 * exportar as quatro listas de uma campanha grande vira um PDF de centenas de
 * páginas, e quase sempre o que se quer é uma delas.
 */
import axios from 'axios';
import { sucesso, erro } from '@/helpers/common/api';

export default {
  name: 'CampanhaRelatorioModal',
  props: {
    visivel: { type: Boolean, default: false },
    campanha: { type: Object, default: null },
    resumo: { type: Object, default: null },
  },
  emits: ['fechar'],
  data() {
    return {
      formato: 'pdf',
      selecionadas: ['kpis', 'agrupamento', 'respondidos'],
      gerando: false,
    };
  },
  computed: {
    kpis() { return (this.resumo && this.resumo.kpis) || {}; },
    secoes() {
      const k = this.kpis;
      return [
        {
          chave: 'kpis',
          titulo: 'Resumo e indicadores',
          descricao: 'Entregues, lidos, taxa de resposta, funil do disparo e em quanto tempo as pessoas responderam.',
          icone: 'ri-dashboard-3-line',
          contagem: null,
        },
        {
          chave: 'agrupamento',
          titulo: 'O que responderam',
          descricao: 'Respostas iguais somadas — uma linha por botão do template e por texto curto repetido.',
          icone: 'ri-pie-chart-2-line',
          contagem: (this.resumo && this.resumo.agrupamento ? this.resumo.agrupamento.length : 0),
          unidade: 'grupo(s)',
        },
        {
          chave: 'respondidos',
          titulo: 'Quem respondeu',
          descricao: 'Nome, cidade, telefone, quando respondeu, quanto tempo depois e o que escreveu.',
          icone: 'ri-chat-smile-2-line',
          contagem: k.respondentes || 0,
          unidade: 'pessoa(s)',
        },
        {
          chave: 'sem_resposta',
          titulo: 'Recebeu e não respondeu',
          descricao: 'A base de quem recebeu o disparo e ficou em silêncio — serve de público para o próximo envio.',
          icone: 'ri-user-search-line',
          contagem: k.semResposta || 0,
          unidade: 'pessoa(s)',
        },
        {
          chave: 'falhas',
          titulo: 'Não recebeu',
          descricao: 'Falhas de envio, sem opt-in e sem telefone, com o motivo de cada uma.',
          icone: 'ri-error-warning-line',
          contagem: (k.falhas || 0) + (k.bloqueados || 0),
          unidade: 'destinatário(s)',
        },
      ];
    },
    nenhumaSecao() { return !this.selecionadas.length; },
  },
  watch: {
    visivel(novo) {
      if (novo) {
        this.formato = 'pdf';
        this.selecionadas = ['kpis', 'agrupamento', 'respondidos'];
      }
    },
  },
  methods: {
    alternar(chave) {
      const i = this.selecionadas.indexOf(chave);
      if (i >= 0) this.selecionadas.splice(i, 1);
      else this.selecionadas.push(chave);
    },
    marcarTudo() {
      this.selecionadas = this.secoes.map((s) => s.chave);
    },
    limparTudo() {
      this.selecionadas = [];
    },
    fechar() {
      if (this.gerando) return;
      this.$emit('fechar');
    },
    nomeDoArquivo(resposta) {
      const cd = resposta.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      return m ? m[1] : null;
    },
    async gerar() {
      if (this.gerando || !this.campanha || this.nenhumaSecao) return;
      this.gerando = true;
      try {
        const params = new URLSearchParams();
        params.set('formato', this.formato);
        params.set('secoes', this.selecionadas.join(','));

        const resposta = await axios.get(
          `/campanha/relatorioRespostas/${this.campanha.id}?${params.toString()}`,
          { responseType: 'blob' },
        );

        const nome = this.nomeDoArquivo(resposta) || `respostas-campanha-${this.campanha.id}.${this.formato}`;
        const url = window.URL.createObjectURL(resposta.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = nome;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        sucesso({ title: `Relatório ${this.formato.toUpperCase()} gerado!` });
        this.$emit('fechar');
      } catch (_e) {
        erro({ title: 'Não foi possível gerar o relatório.' });
      } finally {
        this.gerando = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div v-if="visivel" class="modal-backdrop-custom" @click.self="fechar">
      <div class="modal-card shadow-lg">
        <header class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <h6 class="m-0">
            <i class="ri-file-chart-line me-1"></i>
            Exportar relatório de respostas
          </h6>
          <button type="button" class="btn btn-link p-0 text-body" @click="fechar">
            <i class="ri-close-line" style="font-size: 1.4rem;"></i>
          </button>
        </header>

        <div class="modal-body-custom p-3">
          <p class="text-muted small mb-3">
            Campanha <strong class="text-body">{{ campanha ? campanha.nome : '—' }}</strong>.
            Marque o que deve entrar no arquivo.
          </p>

          <div class="mb-3">
            <label class="form-label small fw-semibold mb-1">Formato</label>
            <div class="btn-group btn-group-sm w-100" role="group">
              <button type="button" class="btn"
                :class="formato === 'pdf' ? 'btn-primary' : 'btn-outline-primary'"
                @click="formato = 'pdf'">
                <i class="ri-file-pdf-2-line me-1"></i> PDF para leitura
              </button>
              <button type="button" class="btn"
                :class="formato === 'csv' ? 'btn-primary' : 'btn-outline-primary'"
                @click="formato = 'csv'">
                <i class="ri-file-excel-2-line me-1"></i> CSV para planilha
              </button>
            </div>
            <small class="text-muted d-block mt-1">
              {{ formato === 'pdf'
                ? 'Documento formatado com a identidade do espaço — para imprimir ou enviar.'
                : 'Uma seção embaixo da outra, separador ponto e vírgula, pronto para o Excel.' }}
            </small>
          </div>

          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="form-label small fw-semibold m-0">Seções do relatório</label>
            <div class="hstack gap-2">
              <button type="button" class="btn btn-link btn-sm p-0" @click="marcarTudo">Marcar todas</button>
              <span class="text-muted">·</span>
              <button type="button" class="btn btn-link btn-sm p-0 text-muted" @click="limparTudo">Limpar</button>
            </div>
          </div>

          <div class="vstack gap-2">
            <button
              v-for="s in secoes"
              :key="s.chave"
              type="button"
              class="secao-item text-start"
              :class="{ ativa: selecionadas.includes(s.chave) }"
              @click="alternar(s.chave)"
            >
              <div class="d-flex align-items-start gap-2">
                <span class="marcador" :class="{ marcado: selecionadas.includes(s.chave) }">
                  <i v-if="selecionadas.includes(s.chave)" class="ri-check-line"></i>
                </span>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center justify-content-between gap-2">
                    <span class="fw-semibold small">
                      <i :class="s.icone" class="me-1"></i>{{ s.titulo }}
                    </span>
                    <span v-if="s.contagem !== null" class="badge bg-body-secondary text-body-secondary">
                      {{ s.contagem }} {{ s.unidade }}
                    </span>
                  </div>
                  <div class="text-muted" style="font-size: 11.5px;">{{ s.descricao }}</div>
                </div>
              </div>
            </button>
          </div>

          <div v-if="nenhumaSecao" class="alert alert-warning py-2 px-3 mt-3 mb-0 small">
            Marque pelo menos uma seção para gerar o arquivo.
          </div>
        </div>

        <footer class="d-flex justify-content-end gap-2 px-3 py-2 border-top">
          <BButton variant="light" size="sm" :disabled="gerando" @click="fechar">Cancelar</BButton>
          <BButton variant="primary" size="sm" :disabled="gerando || nenhumaSecao" @click="gerar">
            <BSpinner v-if="gerando" small class="me-1" />
            <i v-else class="ri-download-2-line me-1"></i>
            {{ gerando ? 'Gerando…' : `Gerar ${formato.toUpperCase()}` }}
          </BButton>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  border-radius: 0.5rem;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.modal-body-custom {
  overflow-y: auto;
  flex: 1;
}
/* Item clicável inteiro (não só a caixinha) — tokens do tema para funcionar
   igual em light e dark mode. */
.secao-item {
  background: var(--vz-secondary-bg, var(--vz-light, #f3f6f9));
  border: 1px solid var(--vz-border-color);
  border-radius: 0.4rem;
  padding: 0.6rem 0.75rem;
  width: 100%;
  color: var(--vz-body-color);
  transition: border-color 0.15s ease, background 0.15s ease;
}
.secao-item:hover {
  border-color: var(--vz-primary);
}
.secao-item.ativa {
  border-color: var(--vz-primary);
  background: rgba(var(--vz-primary-rgb), 0.08);
}
.marcador {
  width: 18px;
  height: 18px;
  min-width: 18px;
  border-radius: 4px;
  border: 1.5px solid var(--vz-border-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-top: 1px;
  background: var(--vz-body-bg);
}
.marcador.marcado {
  background: var(--vz-primary);
  border-color: var(--vz-primary);
  color: #fff;
}
</style>
